import { useActionData, useLoaderData, useNavigate } from "react-router";
import { cva } from "class-variance-authority";
import React, { memo, useEffect, useMemo } from "react";
import { useHydrated } from "remix-utils/use-hydrated";
import { toast } from "sonner";
import { faInfoCircle } from "@insidebeehive/fortawesome/pro-duotone-svg-icons/faInfoCircle";

import ErrorPage from "./error-page";

import ActionButton from "../common/action-button";
import Divider from "../ui/divider";
import { Flex, FlexItem } from "../ui/flex";
import { Icon } from "../ui/icon";
import { Tooltip } from "../ui/tooltip";

import type { FetcherWithComponents } from "react-router";
import type { VariantProps } from "class-variance-authority";
import type { ExternalToast } from "sonner";
import type { ErrorToastType, ToastType, TostObject } from "~/models/interface/common.interface";

import { cn } from "~/lib/utils";
import useViewport from "~/hook/use-viewport";

type actionDataType =
  | {
      status: true;
      toast?: ToastType;
      error?: {
        title: string;
        body: string;
        requestId: string;
      };
      redirect?: string;
    }
  | {
      status: false;
      toast?: ErrorToastType;
      error?: {
        title: string;
        body: string;
        requestId: string;
      };
    };

const isToastObject = (obj: any): obj is TostObject => typeof obj === "object" && obj !== null && (("body" in obj && "id" in obj && "type" in obj) || "title" in obj || "requestId" in obj);

const generateToast = (message: string | React.ReactNode, type: "destructive" | "success" | "loading" | "warning", data?: Omit<ExternalToast, "icon">, requestId?: string | false) => {
  if (type === "success") {
    toast.success(message, { ...data, closeButton: true });
  }
  // Neither a success nor a failure: the action did not fail, but the admin still has to act.
  if (type === "warning") {
    toast.warning(message, { ...data, closeButton: true });
  }
  if (type === "destructive") {
    toast.error(message, {
      ...data,
      closeButton: true,
      ...(requestId && {
        action: {
          label: "Copy",
          onClick: () => {
            requestId && navigator.clipboard.writeText(requestId);
          },
        },
      }),
    });
  }
  if (type === "loading") {
    const toastId = toast.loading(message, {
      ...data,
      closeButton: true,
      id: "loader",
      cancel: {
        label: "Close",
        onClick: () => console.log("Closed!"),
      },
    });
    setTimeout(() => {
      toast.dismiss(toastId);
    }, 2000);
  }
};
export interface PageProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
  fetcher?: FetcherWithComponents<any> | FetcherWithComponents<any>[];
  wrapper?: boolean;
  remixTable?: boolean;
}

const Page = memo(
  React.forwardRef<HTMLDivElement, PageProps>(({ className, fetcher, wrapper, remixTable = false, ...props }, ref) => {
    const actionData: actionDataType | undefined = useActionData();
    const fetcherData: actionDataType | null = Array.isArray(fetcher) ? fetcher?.find((obj) => obj?.state === "idle" && obj?.data)?.data : fetcher?.data && fetcher?.state === "idle" ? fetcher?.data : null;
    const loader: actionDataType = useLoaderData();
    const navigate = useNavigate();
    const viewMode = useViewport();

    // Toast deduplication to prevent multiple toasts within a single request cycle.
    const [shownToasts, setShownToasts] = React.useState<Set<string>>(new Set());

    // Clear shown toasts when component unmounts to prevent memory buildup
    React.useEffect(() => () => setShownToasts(new Set()), []);

    // Reset dedup set on every new fetcher submission so the same error message
    // can appear again on a retry (e.g. wrong password entered twice in a row).
    const fetcherStateForReset = Array.isArray(fetcher) ? fetcher?.find((f) => f?.state !== "idle")?.state : (fetcher as any)?.state;
    React.useEffect(() => {
      if (fetcherStateForReset === "submitting") {
        setShownToasts(new Set());
      }
    }, [fetcherStateForReset]);

    // Helper function to generate unique toast key and check if already shown
    const showToastOnce = React.useCallback(
      (toastData: any, source: string) => {
        if (!toastData) return;

        // Create a stable key by excluding the id field which changes on each request
        const stableToastData = { ...toastData };
        delete stableToastData.id;
        const toastKey = `${source}-${JSON.stringify(stableToastData)}`;
        if (shownToasts.has(toastKey)) return;

        setShownToasts((prev) => {
          const newSet = new Set(prev).add(toastKey);
          // Limit the set size to prevent memory buildup (keep last 100 toasts)
          if (newSet.size > 100) {
            const array = Array.from(newSet);

            return new Set(array.slice(-100));
          }

          return newSet;
        });

        if (isToastObject(toastData)) {
          generateToast(
            toastData?.title,
            toastData?.type,
            {
              description: toastData?.body,
              ...(toastData?.data && toastData?.data),
            },
            (toastData as any)?.requestId,
          );
        }

        if (Array.isArray(toastData)) {
          toastData?.forEach((obj) =>
            generateToast(obj?.title, obj.type, {
              description: obj?.body,
              ...(obj?.data && obj?.data),
            }),
          );
        }
      },
      [shownToasts],
    );

    useEffect(() => {
      if (actionData?.status && actionData?.redirect) {
        navigate(actionData?.redirect);
      }
      if (fetcherData?.status && fetcherData?.redirect) {
        navigate(fetcherData?.redirect);
      }
      if (loader?.status && loader?.redirect) {
        navigate(loader?.redirect);
      }
    }, [actionData?.status, fetcherData?.status]);

    useEffect(() => {
      if (actionData?.toast && !remixTable) {
        showToastOnce(actionData?.toast, "action");
      }
    }, [actionData?.toast, showToastOnce, remixTable]);

    useEffect(() => {
      if (loader?.toast && !remixTable) {
        showToastOnce(loader?.toast, "loader");
      }
    }, [loader?.toast, showToastOnce, remixTable]);

    useEffect(() => {
      if (fetcherData?.toast) {
        showToastOnce(fetcherData?.toast, "fetcher");
      }
    }, [fetcherData?.toast, showToastOnce]);

    const loaderError = useMemo(() => ({ title: loader?.error?.title, message: loader?.error?.body, isError: !!loader?.error?.title, requestId: loader?.error?.requestId }), [loader?.error]);
    const actionError = useMemo(() => ({ title: actionData?.error?.title, message: actionData?.error?.body, isError: !!actionData?.error?.title, requestId: actionData?.error?.requestId }), [actionData?.error]);
    const fetcherError = useMemo(() => ({ title: fetcherData?.error?.title, message: fetcherData?.error?.body, isError: !!fetcherData?.error?.title, requestId: fetcherData?.error?.requestId }), [fetcherData?.error]);
    const errorTitle = useMemo(() => loaderError?.title || actionError?.title || fetcherError?.title, [loaderError?.title, actionError?.title, fetcherError?.title]);
    const errorMessage = useMemo(() => loaderError?.message || actionError?.message || fetcherError?.message, [loaderError?.message, actionError?.message, fetcherError?.message]);
    const isPageError = useMemo(() => loaderError?.isError || actionError?.isError || fetcherError?.isError, [loaderError?.isError, actionError?.isError, fetcherError?.isError]);
    const requestId = useMemo(() => loaderError?.requestId || actionError?.requestId || fetcherError?.requestId, [loaderError?.requestId, actionError?.requestId, fetcherError?.requestId]);

    return isPageError ? (
      <ErrorPage title={errorTitle} message={errorMessage} requestId={requestId} />
    ) : wrapper ? (
      <div ref={ref} {...props} className="w-full" />
    ) : (
      <div ref={ref} className={cn(className, `flex flex-col h-full w-screen ${viewMode === "mobile" ? " max-w-full min-w-[50vw]" : "min-w-[88vw] max-w-full"} `)} {...props} />
    );
  }),
);
Page.displayName = "Page";

export interface HeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
  wrapOnMobile?: boolean;
  backAction?: () => void;
  description?: string;
  subtitle?: string;
  info?: React.ReactNode;
  infoPopover?: React.ReactNode;
}

const Header = React.forwardRef<HTMLDivElement, HeaderProps>(({ title, className, children, backAction, wrapOnMobile, description, subtitle, info, infoPopover, ...props }, ref) => {
  const viewMode = useViewport();

  return (
    <>
      {title ? (
        <div ref={ref} className={cn(className, `p-4 w-full ${wrapOnMobile ? "md:flex" : "flex"} text-wrap items-center md:justify-between sm:px-6 lg:px-8`)} {...props}>
          <FlexItem className={`w-full ${viewMode === "mobile" && title?.length > 25 ? "truncate" : "whitespace-nowrap"}`}>
            <Flex gap={0} alignItems="center" justifyContent="start">
              <h3 className={`${info ? "w-fit mr-2" : "w-full"}  ${viewMode === "mobile" && title?.length > 25 ? "truncate" : "whitespace-nowrap"}`}>{title}</h3>
              {info && (
                <Tooltip title={info} side="right">
                  <span>
                    <Icon icon={faInfoCircle} name="info-circle-duotone" className="text-yellow-600 hover:text-yellow-500 cursor-pointer mr-1" />
                  </span>
                </Tooltip>
              )}
              {infoPopover}
            </Flex>
            {(description || subtitle) && <p>{description || subtitle}</p>}
          </FlexItem>
          {(Boolean(backAction) || Boolean(children)) && (
            <Flex alignItems="center" className={`${wrapOnMobile ? "mt-2 flex-wrap justify-center space-x-2 md:justify-end" : " justify-end"}`}>
              {backAction && <ActionButton type="button" buttonType="back" onClick={backAction} className="mr-2 md:mr-0" />}
              {children}
            </Flex>
          )}
        </div>
      ) : (
        <div ref={ref} className={cn(className, "px-4 py-4 sm:flex sm:items-center sm:justify-between sm:px-6 lg:px-8")} {...props}>
          {children}
        </div>
      )}

      <Divider />
    </>
  );
});
Header.displayName = "Header";

const bodyVariants = cva("grid", {
  variants: {
    gapMobile: {
      px: "p-px",
      0: "p-0",
      1: "p-1",
      2: "p-2",
      3: "p-3",
      4: "p-4",
      5: "p-5",
      6: "p-6",
      7: "p-7",
      8: "p-8",
      9: "p-9",
      10: "p-10",
      11: "p-11",
      12: "p-12",
      14: "p-14",
      16: "p-16",
      20: "p-20",
      24: "p-24",
      28: "p-28",
      32: "p-32",
      36: "p-36",
      40: "p-40",
      44: "p-44",
      48: "p-48",
      52: "p-52",
      56: "p-56",
      60: "p-60",
      64: "p-64",
      72: "p-72",
      80: "p-80",
      96: "p-96",
    },
    gapTab: {
      px: "md:p-px",
      0: "md:p-0",
      1: "md:p-1",
      2: "md:p-2",
      3: "md:p-3",
      4: "md:p-4",
      5: "md:p-5",
      6: "md:p-6",
      7: "md:p-7",
      8: "md:p-8",
      9: "md:p-9",
      10: "md:p-10",
      11: "md:p-11",
      12: "md:p-12",
      14: "md:p-14",
      16: "md:p-16",
      20: "md:p-20",
      24: "md:p-24",
      28: "md:p-28",
      32: "md:p-32",
      36: "md:p-36",
      40: "md:p-40",
      44: "md:p-44",
      48: "md:p-48",
      52: "md:p-52",
      56: "md:p-56",
      60: "md:p-60",
      64: "md:p-64",
      72: "md:p-72",
      80: "md:p-80",
      96: "md:p-96",
    },
    gap: {
      px: "lg:p-px",
      0: "lg:p-0",
      1: "lg:p-1",
      2: "lg:p-2",
      3: "lg:p-3",
      4: "lg:p-4",
      5: "lg:p-5",
      6: "lg:p-6",
      7: "lg:p-7",
      8: "lg:p-8",
      9: "lg:p-9",
      10: "lg:p-10",
      11: "lg:p-11",
      12: "lg:p-12",
      14: "lg:p-14",
      16: "lg:p-16",
      20: "lg:p-20",
      24: "lg:p-24",
      28: "lg:p-28",
      32: "lg:p-32",
      36: "lg:p-36",
      40: "lg:p-40",
      44: "lg:p-44",
      48: "lg:p-48",
      52: "lg:p-52",
      56: "lg:p-56",
      60: "lg:p-60",
      64: "lg:p-64",
      72: "lg:p-72",
      80: "lg:p-80",
      96: "lg:p-96",
    },
  },
  defaultVariants: {
    gap: 8,
    gapMobile: 2,
    gapTab: 2,
  },
});
export interface BodyProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof bodyVariants> {
  asChild?: boolean;
}
const Body = React.forwardRef<HTMLDivElement, BodyProps>(({ gapMobile, gapTab, gap, className, ...props }, ref) => {
  const isHydated = useHydrated();
  if (!isHydated) return <></>;

  return <div ref={ref} className={cn(bodyVariants({ gapMobile, gapTab, gap }), className, " hide-scrollbar flex-1 py-0 overflow-auto flex flex-col")} {...props} />;
});

Body.displayName = "Body";
export { Body, Header, Page };
